import { css, cx } from "@emotion/css";
import { type ReactNode, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
	PrimaryButton,
	TextPrimaryButton,
} from "src/components/Default/LekUI/Button";
import {
	Popper,
	type PopperTriggerProps,
} from "src/components/Default/LekUI/Popper";
import CalendarIcon from "src/images/Icons/newIcons/calendar.svg";

interface BaseDatePickerProps<T> {
	titleKey: string;
	isConfirmDisabled: boolean;
	selectedState: T;
	initialState: T;
	onConfirm: (selected: T, e?: React.MouseEvent) => void;
	children: ReactNode;
	setState: (state: T) => void;
	renderTrigger: (props: PopperTriggerProps) => ReactNode;
	buttonText?: string;
	/** Klucz tłumaczenia przycisku potwierdzenia (domyślnie „Zobacz”). */
	confirmKey?: string;
}

export const BaseDatePicker = <T,>({
	titleKey,
	isConfirmDisabled,
	selectedState,
	initialState,
	onConfirm,
	children,
	setState,
	renderTrigger,
	confirmKey = "datePicker.confirm",
}: BaseDatePickerProps<T>) => {
	const { t } = useTranslation();
	const anchorRef = useRef<HTMLDivElement>(null);

	const onOpen = () => {
		setState(initialState);
	};

	const handleCancel = (event?: React.MouseEvent) => {
		if (
			event &&
			anchorRef.current &&
			anchorRef.current.contains(event.target as Node)
		) {
			return;
		}

		setState(initialState);
	};

	return (
		<div>
			<Popper
				className={cx(
					"p-12",
					css`
            z-index: 99999999;
          `,
				)}
				renderTrigger={renderTrigger}
				onCancel={handleCancel}
				onOpen={onOpen}
			>
				{({ closePopper }: { closePopper: (e: React.MouseEvent) => void }) => (
					<>
						<div className="flex flex-col items-center gap-2 mb-16">
							<CalendarIcon className="icon-normal" />
							<span className="size-16">{t(titleKey)}</span>
						</div>
						<div className="calendar-container p-12">{children}</div>
						<div className="footer mt-16">
							<TextPrimaryButton
								onClick={(e: React.MouseEvent) => {
									handleCancel(e);
									closePopper(e);
								}}
							>
								{t("datePicker.cancel")}
							</TextPrimaryButton>
							<PrimaryButton
								disabled={isConfirmDisabled}
								onClick={(e: React.MouseEvent) => {
									onConfirm(selectedState, e);
									closePopper(e);
								}}
							>
								{t(confirmKey)}
							</PrimaryButton>
						</div>
					</>
				)}
			</Popper>
		</div>
	);
};
